import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

const JUDGEME_BASE = "https://api.judge.me/api/v1";
const SHOP_DOMAIN = "63017a.myshopify.com";

export interface ReviewPicture {
  hidden?: boolean;
  urls: { small?: string; compact?: string; huge?: string; original?: string };
}

export interface JudgeMeReview {
  id: number;
  title: string;
  body: string;
  rating: number;
  product_handle: string;
  product_title: string;
  reviewer: { name: string };
  verified: string;
  curated: "not-yet" | "ok" | "spam";
  hidden: boolean;
  created_at: string;
  has_published_pictures: boolean;
  pictures: ReviewPicture[];
}

export interface PublicReview {
  id: number;
  title: string;
  body: string;
  rating: number;
  reviewerName: string;
  verified: boolean;
  createdAt: string;
  pictures: string[];
}

export interface ProductRating {
  average: number;
  count: number;
}

// Best-effort in-memory cache (module scope persists per isolate on the server).
let cache: { at: number; reviews: JudgeMeReview[] } | null = null;
const CACHE_TTL = 5 * 60 * 1000;

async function fetchAllReviews(token: string, useCache = true): Promise<JudgeMeReview[]> {
  if (useCache && cache && Date.now() - cache.at < CACHE_TTL) return cache.reviews;
  const all: JudgeMeReview[] = [];
  let page = 1;
  // Safety cap; per_page 100 keeps each request small.
  while (page <= 20) {
    const url = `${JUDGEME_BASE}/reviews?shop_domain=${encodeURIComponent(SHOP_DOMAIN)}&per_page=100&page=${page}`;
    const res = await fetch(url, { headers: { "X-Api-Token": token } });
    if (!res.ok) break;
    const data = await res.json();
    const reviews: JudgeMeReview[] = Array.isArray(data?.reviews) ? data.reviews : [];
    all.push(...reviews);
    if (reviews.length < 100) break;
    page++;
  }
  cache = { at: Date.now(), reviews: all };
  return all;
}

function isPublished(r: JudgeMeReview): boolean {
  return r.curated === "ok" && !r.hidden;
}

function isVerified(r: JudgeMeReview): boolean {
  return ["confirmed-buyer", "verified-purchase", "buyer", "semi-verified-purchase"].includes(r.verified);
}

function toPublic(r: JudgeMeReview): PublicReview {
  return {
    id: r.id,
    title: r.title,
    body: r.body,
    rating: r.rating,
    reviewerName: r.reviewer?.name || "Anonymous",
    verified: isVerified(r),
    createdAt: r.created_at,
    pictures: (r.pictures || [])
      .filter((p) => !p.hidden)
      .map((p) => p.urls?.small || p.urls?.compact || p.urls?.original || "")
      .filter(Boolean),
  };
}

/** Verify the caller is an admin using their own Supabase access token (RLS-scoped). */
async function verifyAdmin(accessToken: string | undefined): Promise<boolean> {
  if (!accessToken) return false;
  const url = process.env["SUPABASE_URL"];
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"];
  if (!url || !key) return false;
  const client = createClient<Database>(url, key, {
    auth: { persistSession: false },
    global: { headers: { Authorization: `Bearer ${accessToken}` } },
  });
  const { data } = await client.from("user_roles").select("role").eq("role", "admin").maybeSingle();
  return !!data;
}

// ---- Public: ratings map for product cards ----
export const getProductRatings = createServerFn({ method: "GET" }).handler(async () => {
  const token = process.env["JUDGE_ME_API_TOKEN"];
  if (!token) return { ratings: {} as Record<string, ProductRating> };
  try {
    const reviews = await fetchAllReviews(token);
    const acc: Record<string, { sum: number; count: number }> = {};
    for (const r of reviews) {
      if (!isPublished(r) || !r.product_handle) continue;
      const cur = acc[r.product_handle] || { sum: 0, count: 0 };
      cur.sum += r.rating;
      cur.count += 1;
      acc[r.product_handle] = cur;
    }
    const ratings: Record<string, ProductRating> = {};
    for (const [handle, v] of Object.entries(acc)) {
      ratings[handle] = { average: Math.round((v.sum / v.count) * 10) / 10, count: v.count };
    }
    return { ratings };
  } catch {
    return { ratings: {} as Record<string, ProductRating> };
  }
});

// ---- Public: reviews for one product ----
const reviewsInput = z.object({ handle: z.string() });

export const getProductReviews = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => reviewsInput.parse(input))
  .handler(async ({ data }) => {
    const token = process.env["JUDGE_ME_API_TOKEN"];
    if (!token) return { average: 0, count: 0, reviews: [] as PublicReview[] };
    try {
      const all = await fetchAllReviews(token);
      const published = all.filter((r) => isPublished(r) && r.product_handle === data.handle);
      const reviews = published.map(toPublic);
      const avg = published.length
        ? Math.round((published.reduce((s, r) => s + r.rating, 0) / published.length) * 10) / 10
        : 0;
      return { average: avg, count: published.length, reviews };
    } catch {
      return { average: 0, count: 0, reviews: [] as PublicReview[] };
    }
  });

// ---- Public: submit a web review (Judge.me public endpoint, no token needed) ----
const createInput = z.object({
  shopifyProductId: z.number(),
  handle: z.string(),
  name: z.string().min(1).max(80),
  email: z.string().email(),
  rating: z.number().int().min(1).max(5),
  title: z.string().max(120).optional().default(""),
  body: z.string().min(1).max(5000),
});

export const createReview = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => createInput.parse(input))
  .handler(async ({ data }) => {
    try {
      const res = await fetch(`${JUDGEME_BASE}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shop_domain: SHOP_DOMAIN,
          platform: "shopify",
          id: data.shopifyProductId,
          name: data.name,
          email: data.email,
          rating: data.rating,
          title: data.title,
          body: data.body,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        return { ok: false, error: `Judge.me returned ${res.status}${text ? `: ${text.slice(0, 200)}` : ""}` };
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : "Failed to submit review" };
    }
  });

// ---- Admin: list pending (unpublished) reviews ----
const adminInput = z.object({ accessToken: z.string() });

export const getPendingReviews = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => adminInput.parse(input))
  .handler(async ({ data }) => {
    const token = process.env["JUDGE_ME_API_TOKEN"];
    if (!token) return { reviews: [] as PublicReview[], enabled: false };
    const ok = await verifyAdmin(data.accessToken);
    if (!ok) throw new Error("Forbidden");
    try {
      const all = await fetchAllReviews(token, false);
      const pending = all.filter((r) => r.curated === "not-yet" || (r.curated === "ok" && r.hidden));
      return { reviews: pending.map(toPublic), enabled: true };
    } catch {
      return { reviews: [] as PublicReview[], enabled: true };
    }
  });

// ---- Admin: publish or hide a review ----
const moderateInput = z.object({
  accessToken: z.string(),
  id: z.number(),
  curated: z.enum(["ok", "spam"]),
});

export const moderateReview = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => moderateInput.parse(input))
  .handler(async ({ data }) => {
    const token = process.env["JUDGE_ME_API_TOKEN"];
    if (!token) return { ok: false, error: "Judge.me token not configured" };
    const ok = await verifyAdmin(data.accessToken);
    if (!ok) throw new Error("Forbidden");
    try {
      const res = await fetch(`${JUDGEME_BASE}/reviews/${data.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Api-Token": token },
        body: JSON.stringify({ shop_domain: SHOP_DOMAIN, curated: data.curated }),
      });
      if (!res.ok) {
        return { ok: false, error: `Judge.me returned ${res.status}` };
      }
      // Invalidate the cached list so the next fetch reflects the change.
      cache = null;
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : "Failed to moderate review" };
    }
  });
